/**
 * Presentation contract for the seven document destinations an attorney can use
 * to organise a case. Database and API keys deliberately stay separate from
 * their lawyer-facing labels: `procedure` and `procedures` are distinct values.
 *
 * `correspondances` a rejoint cette liste le 2026-09-25, avec l'arrivée du
 * complément Outlook : un courrier versé au dossier n'est ni une écriture, ni
 * une pièce adverse, et son onglet est monté dans `DossierView`. Il en avait
 * été retiré tant qu'aucune surface ne l'affichait — une pièce y aurait alors
 * disparu de la vue Gestion.
 */
export const DOCUMENT_DESTINATION_ORDER = [
  'nos_pieces',
  'procedure',
  'partie_adverse',
  'procedures',
  'recherche',
  'notes_projets',
  'correspondances',
] as const;

export type DocumentDestinationKey = (typeof DOCUMENT_DESTINATION_ORDER)[number];

/** Ce qu'un onglet du dossier contient, dit à l'avocat comme au modèle. */
export interface DocumentDestinationPresentation {
  label: string;
  definition: string;
  examples: readonly string[];
}

const JURIDICTION_ET_GREFFE = 'Juridiction et greffe';

/**
 * Une seule table pour le libellé, la définition et les exemples de chaque onglet.
 * Les outils de l'agent recopiaient chacun leur propre liste à la main et elles
 * avaient divergé (`procedures` présenté comme une variante de « Mes écritures »,
 * un jugement rangé dans `procedure`) : elles se construisent désormais toutes
 * avec `describeDocumentDestinations` (Weekly produit du 2026-10-30).
 */
export const DOCUMENT_DESTINATION_PRESENTATION: Record<
  DocumentDestinationKey,
  DocumentDestinationPresentation
> = {
  nos_pieces: {
    label: 'Mes pièces',
    definition:
      "Les pièces que l'avocat verse au débat pour son client : elles reçoivent un numéro et figurent au bordereau de communication. Une décision ou une déclaration d'appel que l'avocat communique comme pièce numérotée y reste.",
    examples: ['contrat', 'facture', 'attestation', 'constat', 'échange de courriels produit en preuve'],
  },
  procedure: {
    label: 'Mes écritures',
    definition: `Les actes que l'avocat rédige et dépose pour son client. La déclaration d'appel n'y va pas : elle se range dans « ${JURIDICTION_ET_GREFFE} ».`,
    examples: ['assignation', 'conclusions', 'requête', 'mémoire', 'acte rédigé dans le canvas'],
  },
  partie_adverse: {
    label: 'Partie adverse',
    definition:
      "Ce que l'adversaire communique : ses écritures (sous-catégorie `actes_communiques`) et ses pièces (sous-catégorie `pieces_communiquees`).",
    examples: ['conclusions adverses', 'assignation reçue', 'pièces communiquées par le confrère adverse'],
  },
  procedures: {
    label: JURIDICTION_ET_GREFFE,
    definition:
      "Ce qui émane de la juridiction et du greffe dans ce dossier, et la déclaration d'appel, qui saisit la cour et cadre les écritures, même rédigée par le cabinet.",
    examples: [
      'jugement, arrêt ou ordonnance',
      "déclaration d'appel",
      'ordonnance ou calendrier de la mise en état, avis de clôture',
      "convocation et avis d'audience",
      'avis et notification du greffe (RPVA)',
      "signification d'une décision",
    ],
  },
  recherche: {
    label: 'Mes recherches juridiques',
    definition:
      "La matière juridique réunie pour le dossier. Une décision rendue dans une autre affaire est de la jurisprudence : elle se range ici.",
    examples: ['jurisprudence', 'doctrine', "note d'analyse juridique"],
  },
  notes_projets: {
    label: 'Notes client',
    definition: "Les notes et documents de travail du cabinet, jamais communiqués à l'adversaire.",
    examples: ["compte rendu d'entretien", 'note interne', 'projet ou brouillon', 'consultation'],
  },
  correspondances: {
    label: 'Correspondances',
    definition: `Les échanges du dossier avec le client, l'adversaire ou son conseil, et la juridiction. Un avis ou une convocation du greffe reçu en pièce jointe se range dans « ${JURIDICTION_ET_GREFFE} ».`,
    examples: ['courriel', 'courrier', 'lettre officielle entre avocats'],
  },
};

/** Une ligne de liste : `` - `cle` (« Libellé ») : définition Exemples : a ; b. `` */
export function describeDocumentDestination(key: DocumentDestinationKey): string {
  const { label, definition, examples } = DOCUMENT_DESTINATION_PRESENTATION[key];
  return `- \`${key}\` (« ${label} ») : ${definition} Exemples : ${examples.join(' ; ')}.`;
}

/** Les lignes de `describeDocumentDestination` pour `keys`, dans l'ordre donné. */
export function describeDocumentDestinations(
  keys: readonly DocumentDestinationKey[] = DOCUMENT_DESTINATION_ORDER,
): string {
  return keys.map(describeDocumentDestination).join('\n');
}
